import axiosInstance from "@/api/axios-instance";

// Price tags in admin-written copy (RH-1007). The server owns the list of tags,
// checks copy before it is saved, and previews the finished sentence.

// Where a piece of copy is shown — decides which tags work there.
export type CopySurface = "payment_page" | "reschedule_page" | "scheduling";

// What the copy is about. A pack lets tags name its sessions and quote its
// per-session price; a managed event is what a bare reschedule fee refers to.
export interface CopyContext {
  surface: CopySurface;
  bundleStandardId?: string | null;
  regionId?: string | null;
  managedEventId?: string | null;
  // Copy with no pack of its own (a reschedule fee's page): its product, whose
  // session short names the copy may still quote.
  productId?: string | null;
}

export interface CopyTagOption {
  label: string;
  insert: string; // e.g. "{{RESCHEDULE_PRICE:joint}}"
  description?: string;
}

export interface CopyIssue {
  tag: string;
  message: string;
}

const params = (ctx: CopyContext) => ({
  surface: ctx.surface,
  ...(ctx.bundleStandardId ? { bundle_standard_id: ctx.bundleStandardId } : {}),
  ...(ctx.regionId ? { region_id: ctx.regionId } : {}),
  ...(ctx.productId ? { product_id: ctx.productId } : {}),
});

export async function listCopyTags(ctx: CopyContext): Promise<{
  tags: (CopyTagOption & { key: string })[];
  // hasFee: false → no reschedule fee is attached to that session type in
  // the pack's region, so the tag can't fill in (shown, but not insertable).
  sessions: (CopyTagOption & { name: string; hasFee: boolean })[];
}> {
  return (await axiosInstance.get("/copy-tags", { params: params(ctx) })).data;
}

export async function checkCopy(
  ctx: CopyContext,
  value: unknown,
): Promise<CopyIssue[]> {
  const { data } = await axiosInstance.post("/copy-tags/check", {
    ...params(ctx),
    // Booking copy: named tags must exist on every product using the session.
    ...(ctx.managedEventId ? { managed_event_id: ctx.managedEventId } : {}),
    value,
  });
  return data.issues ?? [];
}

// The products whose bundles use a managed event. Its booking-page copy is
// shared by all of them; the CRM picks one to insert and preview prices with.
export async function listCopyProducts(
  managedEventId: string,
): Promise<{ id: string; name: string }[]> {
  return (
    await axiosInstance.get("/copy-tags/products", {
      params: { managed_event_id: managedEventId },
    })
  ).data;
}

// A pricing cohort: customers who joined on/after `from` (null = before the
// first release). `asOf` is the anchor the preview renders with.
export interface CopyCohort {
  label: string; // "Cohort 1", "Cohort 2", …
  from: string | null;
  asOf: string;
  // What was released at `from`, e.g. "Couple's Therapy - Reschedule Fee ₹ 1,439".
  changes: string[];
}

// One cohort per release date of anything the copy prices — the pack's own
// price versions and its sessions' reschedule fees.
export async function listCopyCohorts(ctx: CopyContext): Promise<CopyCohort[]> {
  return (
    await axiosInstance.get("/copy-tags/cohorts", {
      params: {
        ...(ctx.bundleStandardId ? { bundle_standard_id: ctx.bundleStandardId } : {}),
        ...(ctx.regionId ? { region_id: ctx.regionId } : {}),
        ...(ctx.managedEventId ? { managed_event_id: ctx.managedEventId } : {}),
        ...(ctx.productId ? { product_id: ctx.productId } : {}),
      },
    })
  ).data;
}

export async function previewCopy(
  ctx: CopyContext,
  value: unknown,
  asOf: string,
): Promise<{ value: unknown; issues: CopyIssue[] }> {
  return (
    await axiosInstance.post("/copy-tags/preview", {
      ...params(ctx),
      ...(ctx.managedEventId ? { managed_event_id: ctx.managedEventId } : {}),
      value,
      as_of: asOf,
    })
  ).data;
}

// Whether copy carries any of our tags — skips the server check when not.
export const hasCopyTags = (value: unknown): boolean =>
  /\{\{\s*[A-Z]/.test(typeof value === "string" ? value : JSON.stringify(value ?? ""));

// Run the server check and turn issues into one readable message (or null).
export async function copyIssuesMessage(
  ctx: CopyContext,
  value: unknown,
): Promise<string | null> {
  if (!hasCopyTags(value)) return null;
  const issues = await checkCopy(ctx, value);
  return issues.length
    ? issues.map((i) => `${i.tag} — ${i.message}`).join("\n")
    : null;
}
